import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, fmtMoney } from "../lib/api";
import { PageHeader } from "../components/Shell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DatePicker } from "../components/DatePicker";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  CheckCircle, FloppyDisk, UsersThree, Warning, PencilSimple, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Accounts() {
  const location = useLocation();
  const nav = useNavigate();
  const preselect = new URLSearchParams(location.search).get("dancer") || "";

  const [dancers, setDancers] = useState([]);
  const [dancerId, setDancerId] = useState(preselect);
  const [booking, setBooking] = useState(null);
  const [recentAccounts, setRecentAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);       // {id, ...fields} when editing
  const [editWarnOpen, setEditWarnOpen] = useState(false);  // confirm editing a validated account
  const [pendingEdit, setPendingEdit] = useState(null);
  const [toDelete, setToDelete] = useState(null);     // account pending deletion

  const [f, setF] = useState({
    date_compte: new Date().toISOString().slice(0, 10),
    billets: 0,
    jetons_jaunes: 0,
    jetons_rouges: 0,
    jetons_bleus: 0,
    acompte: 0,
    commentaire: "",
  });

  useEffect(() => {
    api.get("/dancers", { params: { statut: "active" } }).then((r) => setDancers(r.data));
  }, []);

  useEffect(() => {
    if (!dancerId) {
      setBooking(null);
      setRecentAccounts([]);
      return;
    }
    Promise.all([
      api.get(`/dancers/${dancerId}/bookings`),
      api.get(`/dancers/${dancerId}/accounts`),
    ]).then(([b, a]) => {
      setBooking(b.data.find((x) => x.statut === "actif") || null);
      setRecentAccounts(a.data.slice(0, 5));
    });
  }, [dancerId]);

  const calc = useMemo(() => {
    const partBillets = f.billets * 1 * 0.45;
    const partJaunes = f.jetons_jaunes * 20 * 0.45;
    const mRouges = f.jetons_rouges * 10;
    const mBleus = f.jetons_bleus * 5;
    const total = partBillets + partJaunes + mRouges + mBleus;
    const net = total - f.acompte;
    return { partBillets, partJaunes, mRouges, mBleus, total, net };
  }, [f]);

  const selectedDancer = dancers.find((d) => d.id === dancerId);

  const reset = () => {
    setEditing(null);
    setF({
      date_compte: new Date().toISOString().slice(0, 10),
      billets: 0, jetons_jaunes: 0, jetons_rouges: 0, jetons_bleus: 0,
      acompte: 0, commentaire: "",
    });
  };

  const startEdit = (a) => {
    // If already validated, show warning before proceeding.
    if (a.statut_validation === "valide") {
      setPendingEdit(a);
      setEditWarnOpen(true);
      return;
    }
    doStartEdit(a);
  };

  const doStartEdit = (a) => {
    setEditing(a);
    setF({
      date_compte: a.date_compte,
      billets: a.billets || 0,
      jetons_jaunes: a.jetons_jaunes || 0,
      jetons_rouges: a.jetons_rouges || 0,
      jetons_bleus: a.jetons_bleus || 0,
      acompte: a.acompte || 0,
      commentaire: a.commentaire || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info(`Modification du compte du ${new Date(a.date_compte).toLocaleDateString("fr-FR")}`);
  };

  const submit = async (validateAndSend = false) => {
    if (!dancerId) return toast.error("Sélectionnez une danseuse");
    if (!editing && !booking) return toast.error("Cette danseuse n'a pas de booking actif");
    if (calc.total === 0 && f.acompte === 0) return toast.error("Saisissez au moins un montant");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/accounts/${editing.id}`, { ...f, booking_id: editing.booking_id });
        toast.success(`Compte modifié pour ${selectedDancer?.nom_scene}`);
      } else {
        const created = await api.post(`/dancers/${dancerId}/accounts`, { ...f, booking_id: booking.id });
        if (validateAndSend) {
          await api.post(`/accounts/${created.data.id}/validate`, {
            statut_validation: "valide",
            commentaire: "Validé par manageuse à l'enregistrement",
          });
          toast.success(`Compte enregistré et validé pour ${selectedDancer?.nom_scene}`);
        } else {
          toast.success(`Compte enregistré pour ${selectedDancer?.nom_scene}`);
        }
      }
      reset();
      const a = await api.get(`/dancers/${dancerId}/accounts`);
      setRecentAccounts(a.data.slice(0, 5));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/accounts/${toDelete.id}`);
      toast.success("Compte supprimé (récupérable par le Patron)");
      setToDelete(null);
      if (editing?.id === toDelete.id) reset();
      const a = await api.get(`/dancers/${dancerId}/accounts`);
      setRecentAccounts(a.data.slice(0, 5));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de suppression");
    }
  };

  return (
    <div>
      <PageHeader
        testId="accounts-header"
        title="Comptes danseuses"
        subtitle="Saisie rapide du compte journalier. Le calcul se fait en direct."
      />

      {/* Step 1 - dancer selector */}
      <div className="surface p-5 mb-4">
        <div className="flex items-center gap-3 mb-3">
          <StepBadge n={1} active />
          <div className="label-xs">Choisir la danseuse</div>
        </div>
        <Select value={dancerId} onValueChange={setDancerId}>
          <SelectTrigger data-testid="account-dancer-select" className="bg-[#09090b] border-[#27272a] h-12 text-base">
            <SelectValue placeholder="Rechercher / sélectionner..." />
          </SelectTrigger>
          <SelectContent className="bg-[#121217] border-[#27272a] text-white max-h-[400px]">
            {dancers.length === 0 && <div className="px-3 py-2 text-sm text-[#71717a]">Aucune danseuse active</div>}
            {dancers.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                <span className="font-medium">{d.nom_scene}</span>
                <span className="text-[#71717a] ml-2">— {d.prenom} {d.nom}</span>
                {!d.booking_actif && <span className="ml-2 text-[#f87171] text-xs">(pas de booking)</span>}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedDancer && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <InfoPill label="Nom réel" value={`${selectedDancer.prenom} ${selectedDancer.nom}`} />
            <InfoPill label="Booking" value={booking ? `Actif — depuis ${new Date(booking.date_debut).toLocaleDateString("fr-FR")}` : "—"} alert={!booking} />
            <InfoPill label="Solde actuel" value={fmtMoney(selectedDancer.solde_disponible)} tone={selectedDancer.solde_disponible > 0 ? "#34d399" : "#fff"} />
            <InfoPill label="Jours travaillés" value={selectedDancer.jours_travailles} />
          </div>
        )}
      </div>

      {/* Step 2 - entry form */}
      <div className={`surface p-5 mb-4 ${!dancerId || (!editing && !booking) ? "opacity-50 pointer-events-none" : ""}`}>
        <div className="flex items-center gap-3 mb-4">
          <StepBadge n={2} active={!!dancerId && (!!booking || !!editing)} />
          <div className="label-xs">{editing ? "Modifier le compte" : "Saisir les montants de la soirée"}</div>
          {editing && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#fbbf24]/20 text-[#fbbf24] border border-[#fbbf24]/30" data-testid="edit-mode-badge">
                Édition
              </span>
              <Button
                size="sm"
                variant="outline"
                data-testid="cancel-edit"
                onClick={reset}
                className="h-7 border-[#27272a] text-[#a1a1aa] hover:bg-[#1a1a21] text-xs"
              >
                Annuler l'édition
              </Button>
            </div>
          )}
        </div>

        {editing?.statut_validation === "valide" && (
          <div className="flex items-center gap-2 p-3 mb-4 rounded-md border" style={{ borderColor: "#fbbf24", background: "#fbbf241a" }}>
            <Warning size={18} color="#fbbf24" weight="fill" />
            <span className="text-sm text-[#fbbf24]">
              Cette donnée a déjà été validée. Toute modification sera journalisée.
            </span>
          </div>
        )}

        {!booking && dancerId && (
          <div className="flex items-center gap-2 p-3 mb-4 rounded-md border" style={{ borderColor: "#f87171", background: "#f871711a" }}>
            <Warning size={18} color="#f87171" />
            <span className="text-sm text-[#f87171]">Cette danseuse n'a pas de booking actif. Ouvrez un booking depuis sa fiche avant de saisir un compte.</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="md:col-span-1">
            <Label className="label-xs mb-1 block">Date du compte</Label>
            <DatePicker testId="acc-date" value={f.date_compte} onChange={(v) => setF({ ...f, date_compte: v })} className="h-11" />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[
            ["billets", "Billets", "#34d399"],
            ["jetons_jaunes", "Jaunes", "#fbbf24"],
            ["jetons_rouges", "Rouges", "#f87171"],
            ["jetons_bleus", "Bleus", "#60a5fa"],
          ].map(([k, l, c]) => (
            <div key={k} className="surface p-3 !bg-[#09090b]">
              <div className="label-xs mb-2" style={{ color: c }}>{l}</div>
              <Input
                data-testid={`acc-${k}`}
                type="number"
                min="0"
                inputMode="numeric"
                value={f[k]}
                onChange={(e) => setF({ ...f, [k]: Number(e.target.value) })}
                className="bg-transparent border-[#27272a] font-mono text-lg h-12"
              />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="surface p-3 !bg-[#09090b]">
            <Label className="label-xs mb-1 block">Acompte perçu (€)</Label>
            <Input data-testid="acc-acompte" type="number" min="0" inputMode="numeric" value={f.acompte} onChange={(e) => setF({ ...f, acompte: Number(e.target.value) })} className="bg-transparent border-[#27272a] font-mono text-lg h-12" />
          </div>
          <div className="surface p-3 !bg-[#09090b]">
            <Label className="label-xs mb-1 block">Commentaire</Label>
            <Input data-testid="acc-commentaire" value={f.commentaire} onChange={(e) => setF({ ...f, commentaire: e.target.value })} className="bg-transparent border-[#27272a] h-12" placeholder="Optionnel" />
          </div>
        </div>

        {/* Live calc */}
        <div className="rounded-md border p-4" style={{ borderColor: "#27272a", background: "#09090b" }}>
          <div className="label-xs mb-3">Calcul automatique</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm mb-3">
            <CalcLine label="Part billets" value={fmtMoney(calc.partBillets)} color="#34d399" />
            <CalcLine label="Part jaunes" value={fmtMoney(calc.partJaunes)} color="#fbbf24" />
            <CalcLine label="Rouges" value={fmtMoney(calc.mRouges)} color="#f87171" />
            <CalcLine label="Bleus" value={fmtMoney(calc.mBleus)} color="#60a5fa" />
            <CalcLine label="Total dû" value={fmtMoney(calc.total)} color="#fff" />
          </div>
          <div className="flex items-center justify-between border-t pt-3" style={{ borderColor: "#27272a" }}>
            <div>
              <div className="label-xs">Net après acompte</div>
              <div className="text-xs text-[#71717a]">Total dû − acompte</div>
            </div>
            <div data-testid="net-preview" className="font-display text-4xl font-light" style={{ color: "#fbbf24" }}>
              {fmtMoney(calc.net)}
            </div>
          </div>
        </div>
      </div>

      {/* Step 3 - submit */}
      <div className="flex flex-col md:flex-row gap-3 sticky bottom-0 md:bottom-auto p-4 md:p-0 -mx-4 md:mx-0 md:static z-20"
           style={{ background: "rgba(9,9,11,0.9)", backdropFilter: "blur(12px)" }}>
        <Button
          data-testid="submit-account"
          onClick={() => submit(false)}
          disabled={saving || !dancerId || (!editing && !booking)}
          variant="outline"
          className="flex-1 h-14 border-[#fbbf24] text-[#fbbf24] hover:bg-[#fbbf24]/10 text-base font-semibold disabled:opacity-40"
        >
          <FloppyDisk size={20} className="mr-2" />
          {saving ? "..." : editing ? "Enregistrer les modifications" : "Enregistrer"}
        </Button>
        {!editing && (
          <Button
            data-testid="submit-send-account"
            onClick={() => submit(true)}
            disabled={saving || !dancerId || !booking}
            className="flex-1 h-14 bg-[#fbbf24] hover:bg-[#f59e0b] text-black text-base font-semibold disabled:opacity-40"
          >
            <CheckCircle size={20} weight="fill" className="mr-2" />
            {saving ? "..." : "Enregistrer et envoyer"}
          </Button>
        )}
        <Button
          variant="outline"
          onClick={reset}
          className="h-14 border-[#27272a] text-white hover:bg-[#1a1a21] px-8"
        >
          {editing ? "Annuler" : "Réinitialiser"}
        </Button>
      </div>

      {/* Recent accounts */}
      {dancerId && recentAccounts.length > 0 && (
        <div className="mt-8">
          <div className="label-xs mb-3">5 derniers comptes de {selectedDancer?.nom_scene}</div>
          <div className="surface overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: "#27272a" }}>
                  <th className="label-xs text-left py-2.5 px-3">Date</th>
                  <th className="label-xs text-right py-2.5 px-3">Billets</th>
                  <th className="label-xs text-right py-2.5 px-3">Jaunes</th>
                  <th className="label-xs text-right py-2.5 px-3">R</th>
                  <th className="label-xs text-right py-2.5 px-3">B</th>
                  <th className="label-xs text-right py-2.5 px-3">Net</th>
                  <th className="label-xs py-2.5 px-3">Statut</th>
                  <th className="label-xs py-2.5 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentAccounts.map((a) => (
                  <tr key={a.id} className="border-b last:border-b-0" style={{ borderColor: "#27272a" }}>
                    <td className="py-2.5 px-3">{new Date(a.date_compte).toLocaleDateString("fr-FR")}</td>
                    <td className="py-2.5 px-3 text-right font-mono" style={{ color: "#34d399" }}>{a.billets}</td>
                    <td className="py-2.5 px-3 text-right font-mono" style={{ color: "#fbbf24" }}>{a.jetons_jaunes}</td>
                    <td className="py-2.5 px-3 text-right font-mono" style={{ color: "#f87171" }}>{a.jetons_rouges}</td>
                    <td className="py-2.5 px-3 text-right font-mono" style={{ color: "#60a5fa" }}>{a.jetons_bleus}</td>
                    <td className="py-2.5 px-3 text-right font-mono"><b>{fmtMoney(a.total_net_apres_acompte)}</b></td>
                    <td className="py-2.5 px-3 text-xs text-[#a1a1aa] capitalize">{String(a.statut_validation).replace("_", " ")}</td>
                    <td className="py-2.5 px-3">
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 border-[#27272a] hover:bg-[#1a1a21] text-[#fbbf24]"
                          onClick={() => startEdit(a)}
                          data-testid={`edit-account-${a.id}`}
                          title="Modifier"
                        >
                          <PencilSimple size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 border-[#27272a] hover:bg-[#3f1d1d] hover:border-[#f87171] text-[#f87171]"
                          onClick={() => setToDelete(a)}
                          data-testid={`delete-account-${a.id}`}
                          title="Supprimer"
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3">
            <Button variant="outline" onClick={() => nav(`/dancers/${dancerId}`)} className="border-[#27272a] text-white hover:bg-[#1a1a21]">
              <UsersThree size={16} className="mr-2" /> Voir la fiche complète
            </Button>
          </div>
        </div>
      )}

      {/* Confirm editing a validated account */}
      <AlertDialog open={editWarnOpen} onOpenChange={(v) => { if (!v) { setEditWarnOpen(false); setPendingEdit(null); } }}>
        <AlertDialogContent className="bg-[#121217] border-[#27272a] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl flex items-center gap-2">
              <Warning size={22} color="#fbbf24" weight="fill" />
              Modifier un compte validé ?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#a1a1aa]">
              Cette donnée a déjà été validée. Toute modification sera journalisée
              (qui, quoi, quand — consultable par le Patron).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="edit-validated-cancel"
              className="border-[#27272a] text-white hover:bg-[#1a1a21]"
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="edit-validated-confirm"
              className="bg-[#fbbf24] hover:bg-[#f59e0b] text-black"
              onClick={() => {
                if (pendingEdit) doStartEdit(pendingEdit);
                setEditWarnOpen(false);
                setPendingEdit(null);
              }}
            >
              Continuer la modification
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm delete */}
      <AlertDialog open={!!toDelete} onOpenChange={(v) => { if (!v) setToDelete(null); }}>
        <AlertDialogContent className="bg-[#121217] border-[#27272a] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl">
              Confirmer la suppression
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#a1a1aa]">
              Supprimer le compte du <b className="text-white">
                {toDelete ? new Date(toDelete.date_compte).toLocaleDateString("fr-FR") : ""}
              </b> ({toDelete && fmtMoney(toDelete.total_net_apres_acompte)}) ?
              {toDelete?.statut_validation === "valide" && (
                <span className="block mt-2 text-[#fbbf24]">
                  <Warning size={14} className="inline mr-1" weight="fill" />
                  Cette donnée a déjà été validée. La suppression sera journalisée.
                </span>
              )}
              <span className="block mt-2 text-xs">
                Le compte est archivé et récupérable par le Patron (pas supprimé définitivement).
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-account-cancel"
              className="border-[#27272a] text-white hover:bg-[#1a1a21]"
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-account-confirm"
              className="bg-[#f87171] hover:bg-[#ef4444] text-white"
              onClick={confirmDelete}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StepBadge({ n, active }) {
  return (
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold"
      style={{
        background: active ? "#fbbf24" : "#27272a",
        color: active ? "#09090b" : "#71717a",
      }}
    >
      {n}
    </div>
  );
}

function InfoPill({ label, value, tone = "#fff", alert = false }) {
  return (
    <div className="rounded-md border px-3 py-2" style={{ borderColor: alert ? "#f87171" : "#27272a", background: "#09090b" }}>
      <div className="label-xs" style={{ color: alert ? "#f87171" : "#71717a" }}>{label}</div>
      <div className="text-sm mt-0.5 truncate" style={{ color: tone }}>{value}</div>
    </div>
  );
}

function CalcLine({ label, value, color }) {
  return (
    <div>
      <div className="label-xs">{label}</div>
      <div className="font-mono text-sm mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
